import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BsInfoCircle } from 'react-icons/bs';
import { FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { STRIPE_STATUS_MESSAGES } from '../utils/constants';

import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { haggleApiService } from '@/services/haggle.service';

export interface IStripeAccountStatus {
	chargesEnabled: boolean;
	payoutsEnabled: boolean;
	detailsSubmitted: boolean;
	disabledReason: string | null;
	pendingRequirements: string[];
	status: string;
	updateLink: string | null;
}

interface Country {
	code: string;
	name: string;
}

enum StripeConnectStatus {
	enabled = 'enabled',
	restricted = 'restricted',
	notCreated = 'not_created',
	pending = 'pending',
}

export const StripeConnectButton = ({
	isSizeCompact,
}: {
	isSizeCompact: boolean;
}) => {
	const { user, isLoading } = useUser();
	const [isConnected, setIsConnected] = useState(false);
	const [showDetails, setShowDetails] = useState(false);
	const [linkGenerateProgress, setLinkGenerateProgress] = useState(false);
	const [checkStripeAccountProgress, setCheckStripeAccountProgress] =
		useState(false);
	const [checking, setChecking] = useState(true);
	const [stripeAccountStatus, setStripeAccountStatus] =
		useState<IStripeAccountStatus | null>(null);
	const [selectedCountry, setSelectedCountry] = useState<string>('AU');
	const [error, setError] = useState<string | null>(null);

	const { data: countries, isLoading: isCountriesLoading } = useQuery<
		Country[]
	>({
		queryKey: [ApiQueryKeys.getSupportedStripeCountries],
		queryFn: () => haggleApiService.fetchSupportedStripeCountries(),
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		const checkStatus = async () => {
			try {
				const res = await haggleApiService.getStripeAccountStatus();
				setStripeAccountStatus(res);
				setIsConnected(res.chargesEnabled && res.payoutsEnabled);
			} catch (err) {
				console.error('Failed to check Stripe account status:', err);
				setError('Could not check account status.');
			} finally {
				setChecking(false);
			}
		};

		if (user && !isLoading) checkStatus();
	}, [user, isLoading]);

	const handleConnect = async () => {
		setLinkGenerateProgress(true);
		try {
			const res = await haggleApiService.getStripeOnboardingLink(
				selectedCountry,
			);
			window.location.href = res.url;
			setLinkGenerateProgress(false);
		} catch (err) {
			setError('Unable to connect to Stripe. Please try again later.');
			setLinkGenerateProgress(false);
		}
	};

	const redirectToUpdateOnboardingLink = () => {
		setLinkGenerateProgress(true);
		setTimeout(() => {
			if (stripeAccountStatus && stripeAccountStatus.updateLink) {
				window.location.href = stripeAccountStatus.updateLink;
			}
			setLinkGenerateProgress(false);
		}, 1000);
	};

	const checkExistingConnectAccount = async () => {
		if (user) {
			setCheckStripeAccountProgress(true);
			try {
				const response = await haggleApiService.checkExistingConnectAccount(
					user.username,
				);
				if (response.accounts) {
					// Refresh the Stripe account status after successful linking
					const updatedStatus = await haggleApiService.getStripeAccountStatus();
					setStripeAccountStatus(updatedStatus);
					setIsConnected(
						updatedStatus.chargesEnabled && updatedStatus.payoutsEnabled,
					);

					setCheckStripeAccountProgress(false);
					toast.success(
						'Your existing Stripe connect account has been linked successfully.',
					);
				} else {
					setCheckStripeAccountProgress(false);
					toast.error(
						'No existing Stripe connect account found for this user. You have to create a new account by clicking on "Add Bank Details" button.',
					);
				}
			} catch (err) {
				setCheckStripeAccountProgress(false);
				console.error('Failed to check existing connect account:', err);
				toast.error(
					'Failed to check existing Stripe account. Please try again.',
				);
			}
		}
	};

	if (checking) return <p>Checking Stripe status...</p>;

	return (
		<div className="bg-amber-100 border border-amber-300 text-amber-900 rounded-lg shadow px-4 py-3">
			{stripeAccountStatus?.status === StripeConnectStatus.pending ? (
				<>
					<div className="flex items-start gap-2 mb-2">
						<BsInfoCircle
							className="text-yellow-600 text-xl"
							aria-hidden="true"
						/>
						<p className="text-yellow-900 font-medium">
							{STRIPE_STATUS_MESSAGES.pending}
						</p>
					</div>
				</>
			) : (
				<>
					{isConnected ? (
						<p className="text-purpleBrand-500">
							{STRIPE_STATUS_MESSAGES.complete}
						</p>
					) : (
						<>
							<div className="flex items-start gap-2 mb-2">
								<BsInfoCircle
									className="text-yellow-600 text-xl"
									aria-hidden="true"
								/>
								<p className="text-yellow-900 font-medium">
									{stripeAccountStatus?.pendingRequirements.length === 0
										? STRIPE_STATUS_MESSAGES.create
										: STRIPE_STATUS_MESSAGES.incomplete}
								</p>
							</div>
							<div className="gap-4">
								{isCountriesLoading && (
									<div className="flex justify-end items-center mt-4 gap-2">
										<FaSpinner
											size={20}
											className="animate-spin text-gray-600"
										/>
										<span className="text-gray-600">Fetching details...</span>
									</div>
								)}

								{stripeAccountStatus &&
									stripeAccountStatus?.pendingRequirements.length === 0 &&
									countries &&
									countries.length > 0 && (
										<div
											className={`${
												isSizeCompact
													? 'flex flex-col gap-2'
													: 'flex justify-end items-center gap-4'
											}`}
										>
											<div className="flex justify-end items-center gap-4">
												<select
													value={selectedCountry}
													onChange={(e) => setSelectedCountry(e.target.value)}
													className={`h-10 px-1 py-2 border border-gray-300 rounded text-md focus:outline-none focus:ring-0 ${
														isSizeCompact ? 'flex-1 w-full' : ''
													}`}
												>
													<option value="">Select Country</option>
													{countries.map((country) => (
														<option key={country.code} value={country.code}>
															{country.name}
														</option>
													))}
												</select>
												<button
													onClick={handleConnect}
													disabled={!selectedCountry}
													className={`px-4 py-2 rounded font-semibold transition flex justify-center items-center ${
														selectedCountry
															? 'bg-blue-600 text-white hover:bg-blue-700'
															: 'bg-gray-300 text-gray-600 cursor-not-allowed'
													}`}
												>
													{linkGenerateProgress ? (
														<FaSpinner className="text-xl animate-spin text-white mr-2" />
													) : null}
													<span className={`${isSizeCompact ? 'text-sm' : ''}`}>
														{linkGenerateProgress
															? 'Processing...'
															: 'Add Bank Details'}
													</span>
												</button>
											</div>
											<div className="text-center text-yellow-900 font-medium text-md">
												OR
											</div>
											<div
												className={`${
													isSizeCompact ? 'flex justify-center mb-2' : ''
												}`}
											>
												<button
													onClick={checkExistingConnectAccount}
													disabled={checkStripeAccountProgress}
													className={`px-4 py-2 rounded font-semibold transition flex justify-center items-center ${
														checkStripeAccountProgress
															? 'bg-blue-400 cursor-not-allowed'
															: 'bg-blue-600 hover:bg-blue-700'
													} text-white`}
												>
													{checkStripeAccountProgress && (
														<FaSpinner className="text-xl animate-spin text-white mr-2" />
													)}
													<span className={`${isSizeCompact ? 'text-sm' : ''}`}>
														{checkStripeAccountProgress
															? 'Linking to existing account...'
															: 'Connect Existing Stripe Account'}
													</span>
												</button>
											</div>
										</div>
									)}

								{stripeAccountStatus &&
									stripeAccountStatus?.pendingRequirements.length > 0 &&
									countries &&
									countries.length > 0 && (
										<div className="w-full px-4 py-3">
											<div className="flex justify-between items-center">
												<span
													onClick={() => setShowDetails(!showDetails)}
													className="underline cursor-pointer font-medium"
												>
													{showDetails ? 'Hide Details' : 'View Details'}
												</span>
												<button
													onClick={redirectToUpdateOnboardingLink}
													className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded flex justify-center items-center"
												>
													{linkGenerateProgress ? (
														<FaSpinner className="text-xl animate-spin text-white mr-2" />
													) : null}
													<span>
														{linkGenerateProgress
															? 'Processing...'
															: 'Complete Onboarding'}
													</span>
												</button>
											</div>

											{showDetails && (
												<div
													className={`overflow-hidden transition-all duration-500 ease-in-out mt-4 ${
														showDetails
															? 'max-h-96 opacity-100'
															: 'max-h-0 opacity-0'
													}`}
												>
													<ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-2 list-disc list-inside text-sm text-yellow-900">
														{stripeAccountStatus.pendingRequirements.map(
															(req, idx) => (
																<li
																	key={idx}
																	className="overflow-hidden whitespace-nowrap text-ellipsis"
																	title={req}
																>
																	{req.replace(/[-._]/g, ' ')}
																</li>
															),
														)}
													</ul>
												</div>
											)}
										</div>
									)}
							</div>
							{error && <p className="text-red-600 mt-2 text-sm">{error}</p>}
						</>
					)}
				</>
			)}
		</div>
	);
};
